import { WSFilterOption } from "@wingspanhq/fe-component-library";
import {
  PayableStatus,
  PayerApprovalStatus,
  SearchPayableRowsFilterParameter
} from "@wingspanhq/wingspan-api-sdk";

export const STATUS_FILTER_ALL = "All";

export type PayablesStatusFilterValue =
  | typeof STATUS_FILTER_ALL
  | "draft"
  | "pendingApproval"
  | "eligibilityPending"
  | "scheduled"
  | "paid";

type PayablesStatusFilterKey = Exclude<
  PayablesStatusFilterValue,
  typeof STATUS_FILTER_ALL
>;

/**
 * The `searchPayableRows` fields a status option constrains. There is no single
 * v3 payable status behind the payer-facing states: each one is a compound of
 * the raw `PayableStatus` lifecycle and the `PayerApprovalStatus` workflow, so
 * an option always sets both (`undefined` clears the tab's own constraint).
 */
type StatusClause = Pick<
  SearchPayableRowsFilterParameter,
  "status" | "payerApprovalStatus"
>;

const NOT_YET_APPROVED = [
  PayerApprovalStatus.PENDING,
  PayerApprovalStatus.PRE_APPROVED
];

/**
 * One clause per pill `getPayableStatusCellProps` can render, which keeps the
 * options disjoint — unlike the tabs, whose pre-payment buckets show a union of
 * `Pending` (awaiting contractor) and `Opened` (awaiting the payer) rows.
 *
 * "Overdue" is deliberately absent (PLA-3648): v3 has no Overdue status, and
 * expressing it as `Opened` + a past `dueDate` would scope `dueDate` across the
 * whole request rather than just that option.
 */
export const PAYABLES_STATUS_CLAUSES: Record<
  PayablesStatusFilterKey,
  StatusClause
> = {
  draft: {
    status: { anyOf: [PayableStatus.CREATED] },
    payerApprovalStatus: undefined
  },
  pendingApproval: {
    status: { anyOf: [PayableStatus.OPENED] },
    payerApprovalStatus: { anyOf: NOT_YET_APPROVED }
  },
  eligibilityPending: {
    status: { anyOf: [PayableStatus.PENDING] },
    payerApprovalStatus: { anyOf: NOT_YET_APPROVED }
  },
  scheduled: {
    status: { anyOf: [PayableStatus.PENDING, PayableStatus.OPENED] },
    payerApprovalStatus: { anyOf: [PayerApprovalStatus.APPROVED] }
  },
  paid: {
    status: {
      anyOf: [PayableStatus.PAID, PayableStatus.PAYMENT_IN_TRANSIT]
    },
    payerApprovalStatus: undefined
  }
};

export const PAYABLES_STATUS_OPTIONS: WSFilterOption<"radio">[] = [
  { name: STATUS_FILTER_ALL, label: "All", hidePill: true },
  { name: "draft", label: "Draft" },
  { name: "pendingApproval", label: "Pending approval" },
  { name: "eligibilityPending", label: "Payments eligibility pending" },
  { name: "scheduled", label: "Scheduled" },
  { name: "paid", label: "Paid" }
];

export const isActiveStatusFilter = (
  value: PayablesStatusFilterValue | undefined
): value is PayablesStatusFilterKey => !!value && value !== STATUS_FILTER_ALL;
